import React, { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../../../hooks/useFetch";

const PDGiftRedeem = ({ 
  product, 
  onClose, 
  countryCode, 
  currency,
  purchasePayload,
  onRedeemSuccess // Optional callback
}) => {
  const authState = useSelector((state) => state.authReducer);
  const token = authState?.token;
  const userId = authState.user?._id;

  const [fetchData] = useFetch();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const [couponCode, setCouponCode] = useState("");
  const [myCouponStatus, setMyCouponStatus] = useState(false);
  const [couponStatusMsg, setCouponStatusMsg] = useState("");
  const [couponData, setCouponData] = useState(null);

  const fetchMyGiftCoupon = useCallback(async (code) => {
    if (!code) return { valid: false };

    const config = {
      url: "/pdgiftcoupon/verify",
      method: "post",
      headers: { Authorization: token },
      data: {
        couponCode: code,
        product,
        recipientId: userId,
        status: "CREATED",
      },
    };

    try {
      setLoading(true);
      const response = await fetchData(config, { showSuccessToast: false });

      if (response?.coupon) {
        setCouponData(response.coupon);
        setMyCouponStatus(true);
        setCouponStatusMsg(`🎉 Coupon verified: ${response.coupon.couponCode}`);
        return { valid: true, coupon: response.coupon };
      } 

      setMyCouponStatus(false);
      setCouponStatusMsg("❌ Coupon code either not found / redeemed!!");
      return { valid: false };
    } catch (err) {
      console.error("Verify error:", err);
      setMyCouponStatus(false);
      setCouponStatusMsg("❌ Error verifying coupon");
      return { valid: false };
    } finally {
      setLoading(false);
    }
  }, [token, fetchData, product, userId]);

  const handleVerify = () => {
    setError("");
    const trimmed = couponCode.trim().toUpperCase();
    
    if (!trimmed || trimmed.length < 13) {
      setError("Invalid coupon code");
      return;
    }

    fetchMyGiftCoupon(trimmed);
  };

  const handleRedeem = async () => {
    if (loading || !myCouponStatus || !couponData?._id) return;

    setError("");
    setLoading(true);

    try {
      // Minimal payload as per architecture
      const redeemConfig = {
        // url: "/pdgiftcoupon/redeem",
        url: "/pd/subscription/redeem",
        method: "post",
        headers: { Authorization: token },
        data: {
          ...purchasePayload,

          couponId: couponData._id,

          isGifted: true,

          giftedByName: couponData.giftedByName,
          giftedByCode: couponData.giftedByCode,
          giftedDate: couponData.createdAt,
          giftMsg: couponData.giftMessage,

          recipientId: purchasePayload.userId,

          redeemedAt: new Date().toISOString(),

          // status: "REDEEMED",
        },
      };

      const response = await fetchData(redeemConfig, { silent: true });

        if (response?.status === true) {           // ← Changed to check response.status
            setSuccess(response.message || "Gift redeemed successfully!");
            setCouponStatusMsg("🎁 Gift redeemed & Subscription activated successfully!");
            setMyCouponStatus(false);

            onRedeemSuccess?.({
                coupon: couponData,
                subscription: response.data,
                message: response.message,
                taxMeta: response.taxMeta
            });
        } else if (response?.wasRedeemed) {
            setCouponStatusMsg("This coupon was already redeemed");
        } else {
            setError(response?.message || "Failed to redeem gift");
        }

      // Success
      setSuccess(response.message || "Gift redeemed successfully!");
      setCouponStatusMsg("🎁 Gift redeemed & Subscription activated successfully!");
      setMyCouponStatus(false);

      onRedeemSuccess?.({
        coupon: couponData,
        message: response.message,
      });

    } catch (err) {
      console.error("Redeem error:", err);
      setError(err?.response?.data?.message || "Failed to redeem gift");
      setCouponStatusMsg("❌ Redemption failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#354567] border border-purple-500/20 rounded-2xl p-6">
      {/* <div className="flex justify-end mb-6">
        <button
          onClick={onClose}
          className="w-fit text-gray-100 bg-blue-700 hover:bg-blue-600 text-xs py-1 px-4 rounded"
        >
          ✖ Close
        </button>
      </div> */}

      <div className="text-center mb-2">
        {/* <div className="text-xl lg:text-2xl mb-3">🎁</div>
        <h2 className="text-lg font-semibold text-purple-300">Redeem Gift Coupon</h2> */}
        <p className="text-xs text-gray-200 mt-1">
          Enter your coupon code to redeem the gift
        </p>
      </div>

      <input
        value={couponCode}
        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
        placeholder="ENTER COUPON CODE"
        className="w-full px-2 py-1 rounded-lg border border-gray-400 bg-gray-900 text-white text-center tracking-widest text-xs focus:outline-none focus:ring-2 focus:ring-purple-500"
        disabled={myCouponStatus}
      />

      {error && <p className="text-red-400 text-sm mt-3 text-center">{error}</p>}

      <div className="flex flex-row justify-center mt-2">
        <button
          onClick={onClose}
          className="w-fit text-gray-100 bg-blue-700 hover:bg-blue-600 text-xs py-1 px-4 rounded-xl"
        >
          ✖ Close
        </button>
        <button
          onClick={handleVerify}
          disabled={loading || success || myCouponStatus || !couponCode.trim()}
          className={`w-fit px-4 py-1 rounded-xl text-sm font-medium transition-all ${
            myCouponStatus
              ? "bg-green-700 cursor-default"
              : "bg-purple-600 hover:bg-purple-700"
          }`}
        >
          {myCouponStatus ? "Verified ✅" : "Verify Coupon"}
        </button>
      </div>

      {couponStatusMsg && (
        <p className="text-center text-sm mt-2 text-gray-300">{couponStatusMsg}</p>
      )}

      {myCouponStatus && couponData && (
        <div className="mt-6 bg-green-900/20 border border-green-500/30 p-5 rounded-2xl">
          <p className="text-white text-center">🎁 From: {couponData.gifterName}</p>
          <p className="text-yellow-300 text-center mt-2">Product: {couponData.product}</p>
          {couponData.giftMessage && (
            <div className="italic text-purple-200 text-center mt-3">
              "{couponData.giftMessage}"
            </div>
          )}
        </div>
      )}

      {myCouponStatus && (
        <div className="flex flex-col items-center mt-8">
          <button
            onClick={handleRedeem}
            disabled={loading || success}
            className="w-fit px-4 py-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 rounded-2xl text-base font-semibold transition-all"
          >
            {loading ? "Redeeming..." : "Redeem Gift"}
          </button>
        </div>
      )}

      {success && (
        <p className="text-green-400 text-center mt-4 font-medium">{success}</p>
      )}
    </div>
  );
};

export default PDGiftRedeem;